import { ComboBox, type FieldOwnOptions, MultiSelect, Select } from "@patient-watch/forms";
import type { SupportedLocale } from "@patient-watch/i18n";
import { useMemo } from "react";
import { optionsUnderParent, type ResolvedDictionary, type ResolvedOption } from "../resolveDictionary";
import { toFormOptions } from "../toFormOptions";
import { useAutoSelectSoleOption } from "../useAutoSelectSoleOption";

export type DictionaryControl = "auto" | "select" | "combobox" | "multiple";

interface DictionaryFieldProps {
    name: string;
    dictionary: ResolvedDictionary;
    locale: SupportedLocale;
    /** Narrow the list, for example to the children of the previous step in a chain. Defaults to the top level. */
    options?: ResolvedOption[];
    /** `auto` follows the dictionary: multiple, then searchable, then a plain Select. */
    control?: DictionaryControl;
    label?: string;
    description?: string;
    disabled?: boolean;
    required?: boolean;
    /** Overrides the dictionary's own clear-button setting. */
    clearable?: boolean;
    placeholder?: string;
    /** If the filtered option list has one row, set the field to that value (chained lists). */
    autoSelectSoleOption?: boolean;
    /** "Add your own option", when the question allows it. Not offered on a multi-select. */
    ownOptions?: FieldOwnOptions;
}

export function pickControl(
    dictionary: Pick<ResolvedDictionary, "isMultiple" | "isSearchable">,
    control: DictionaryControl = "auto"
): Exclude<DictionaryControl, "auto"> {
    if (control !== "auto") return control;
    if (dictionary.isMultiple) return "multiple";
    return dictionary.isSearchable ? "combobox" : "select";
}

/** One field, driven by a dictionary: its options, order, groups, copy and control flags come from the database. */
export function DictionaryField({
    name,
    dictionary,
    locale,
    options,
    control = "auto",
    label,
    description,
    disabled,
    required,
    clearable,
    placeholder,
    autoSelectSoleOption = false,
    ownOptions,
}: DictionaryFieldProps) {
    const visible = useMemo(
        () => options ?? optionsUnderParent(dictionary.options, null),
        [options, dictionary.options]
    );
    useAutoSelectSoleOption({
        name,
        options: visible,
        enabled: autoSelectSoleOption && !disabled,
    });
    const formOptions = useMemo(() => toFormOptions(visible), [visible]);
    const resolvedPlaceholder = placeholder ?? dictionary.placeholder;
    const isClearable = clearable ?? dictionary.isClearable;
    const kind = pickControl(dictionary, control);

    if (kind === "multiple") {
        return (
            <MultiSelect
                name={name}
                label={label}
                description={description}
                options={formOptions}
                disabled={disabled}
                trigger={resolvedPlaceholder ?? "Select options"}
            />
        );
    }

    if (kind === "combobox") {
        return (
            <ComboBox
                name={name}
                label={label}
                description={description}
                options={formOptions}
                disabled={disabled}
                clearable={isClearable}
                placeholder={resolvedPlaceholder}
                searchPlaceholder={dictionary.searchPlaceholder}
                emptyText={dictionary.emptyText}
                locale={locale}
                ownOptions={ownOptions}
            />
        );
    }

    return (
        <Select
            name={name}
            label={label}
            description={description}
            options={formOptions}
            disabled={disabled}
            required={required}
            clearable={isClearable}
            placeholder={resolvedPlaceholder}
            preserveOrder
            ownOptions={ownOptions}
        />
    );
}
